/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useRef } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ChartOptions,
} from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  annotationPlugin,
);

interface IBitrateChartProps {
  color: string;
  data: number[];
  labels?: string[];
  lines: number[];
  title: string;
}

const ChartContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  display: 'inline-block',
  padding: theme.spacing(1),
}));

const ChartArea = styled(Box)(({ theme }) => ({
  display: 'block',
  background: 'rgba(255, 255, 255, 0.04)',
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius,
  height: '300px', // Set a fixed height
}));

const ChartTitle = styled(Typography)(({ theme }) => ({
  textAlign: 'center',
  fontSize: '1.2em',
  marginBottom: theme.spacing(1),
}));

export const BitrateChart: React.FC<IBitrateChartProps> = ({
  color,
  data,
  labels,
  lines,
  title,
}) => {
  const chartRef = useRef<any>(null);
  const maxValue = Math.max(...data) * 1.1;

  useEffect(() => {
    const timer = setTimeout(() => {
      if (chartRef.current) {
        chartRef.current.update();
      }
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  const chartData = {
    labels: labels || data.map((_, index) => index.toString()),
    datasets: [
      {
        label: title,
        data,
        borderColor: `rgba(${color},1)`,
        backgroundColor: (context: any) => {
          const { ctx } = context.chart;
          const gradient = ctx.createLinearGradient(0, 400, 0, 50);
          gradient.addColorStop(1, `rgba(${color},0.15)`);
          gradient.addColorStop(0.5, `rgba(${color},0.0)`);
          gradient.addColorStop(0, `rgba(${color},0)`);
          return gradient;
        },
        borderWidth: 2,
        pointRadius: 0,
        fill: true,
        tension: 0.4,
      },
    ],
  };

  const options: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        beginAtZero: true,
        max: maxValue,
        title: {
          display: true,
          text: 'Kbps',
        },
      },
      x: {
        display: false,
        title: {
          display: false,
          text: 'Time',
        },
        grid: {
          display: false,
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      annotation: {
        annotations: {
          line1: {
            type: 'line',
            yMin: lines[0],
            yMax: lines[0],
            borderColor: 'rgba(255, 0, 0, 0.5)',
            borderWidth: 2,
            borderDash: [5, 5],
          },
          line2: {
            type: 'line',
            yMin: lines[1],
            yMax: lines[1],
            borderColor: 'rgba(255, 200, 0, 0.5)',
            borderWidth: 2,
            borderDash: [5, 5],
          },
          line3: {
            type: 'line',
            yMin: lines[2],
            yMax: lines[2],
            borderColor: 'rgba(0, 200, 0, 0.5)',
            borderWidth: 2,
            borderDash: [5, 5],
          },
        },
      },
    },
    animation: {
      duration: 0, // general animation time
    },
    // responsiveAnimationDuration: 0, // animation duration after a resize
    elements: {
      line: {
        tension: 0, // disables bezier curves
      },
    },
  };

  return (
    <ChartContainer>
      <ChartTitle>{title}</ChartTitle>
      <ChartArea>
        <Line data={chartData} options={options} ref={chartRef} />
      </ChartArea>
    </ChartContainer>
  );
};
